import type { VoltageDropResult } from '../../types/engineering.ts'
import { VDROP_COLORS } from '../../types/engineering.ts'
import { AsOfStamp } from './AsOfStamp.tsx'
import { Term } from '../common/Term.tsx'
import { AssumptionsDisclosure } from './AssumptionsDisclosure.tsx'

interface VoltageDropSummaryProps {
  data: VoltageDropResult | undefined
  isLoading: boolean
}

export function VoltageDropSummary({ data, isLoading }: VoltageDropSummaryProps) {
  if (isLoading) {
    return <div className="text-sm text-gray-400 p-2">Calculating voltage drop...</div>
  }

  if (!data || data.total_segments === 0) {
    return <div className="text-sm text-gray-400 p-2">No conductor segments for this feeder</div>
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Voltage Drop</h3>
        <span className="text-xs text-gray-500">
          {data.nominal_voltage_kv} kV nominal
        </span>
      </div>
      <AsOfStamp computedAt={null} />
      <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
        Tier 2 approximation — spatial load placement, simplified impedance model. Not a substitute for full power flow.
      </div>

      {/* Reachability diagnostics (spec §3.5): dropped load must be visible. */}
      {(data.unreachable_transformers > 0 || data.connected_components > 1) && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          {data.connected_components} connected components on this feeder —{' '}
          {data.unreachable_transformers} transformer
          {data.unreachable_transformers === 1 ? '' : 's'} (
          {data.unreachable_load_kw.toFixed(1)} kW of load) unreachable from the
          source and NOT included in the drop analysis. Islanded rows below show
          0.0 pu and are artifacts of missing connectivity, not real voltages.
        </div>
      )}

      {/* Review E5: load that never entered the model at all. */}
      {data.unplaceable_transformers > 0 && (
        <div className="text-xs text-gray-700 bg-gray-50 border border-gray-300 rounded px-2 py-1">
          {data.unplaceable_transformers} transformer
          {data.unplaceable_transformers === 1 ? '' : 's'} could not be placed on
          this feeder&rsquo;s conductors (nothing within 100 ft). Their load is NOT
          in this analysis and their voltage shows as &mdash;.
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded p-2 text-center">
          <div className="text-lg font-bold" style={{ color: data.max_drop_pct > 5 ? VDROP_COLORS.violation : data.max_drop_pct > 3 ? VDROP_COLORS.caution : VDROP_COLORS.normal }}>
            {data.max_drop_pct.toFixed(2)}%
          </div>
          <div className="text-xs text-gray-500">Max Drop</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2 text-center">
          <div className="text-lg font-bold text-red-600">{data.violation_count}</div>
          <div className="text-xs text-gray-500">Violations (&gt;5%)</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2 text-center">
          <div className="text-lg font-bold text-yellow-600">{data.caution_count}</div>
          <div className="text-xs text-gray-500">Cautions (3-5%)</div>
        </div>
      </div>

      {/* Segments info */}
      <div className="text-xs text-gray-500">
        {data.total_segments} segments analyzed
        {data.segments_missing_impedance > 0 && (
          <span className="ml-1 text-orange-500">
            ({data.segments_missing_impedance} using default impedance)
          </span>
        )}
      </div>

      {/* A3 (spec §3.5): the modelling assumptions this run actually used. */}
      <AssumptionsDisclosure assumptions={data.assumptions ?? []} />

      {/* Transformer drops */}
      {data.transformer_drops.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            Transformer Voltage Levels
          </h4>
          <div className="overflow-x-auto max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="pb-1 pr-2">Transformer</th>
                  <th className="pb-1 pr-2 text-right"><Term k="kva">kVA</Term></th>
                  <th className="pb-1 pr-2 text-right">Meters</th>
                  <th className="pb-1 pr-2 text-right">Drop %</th>
                  <th className="pb-1 pr-2 text-right"><Term k="pu">Voltage p.u.</Term></th>
                  <th className="pb-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.transformer_drops.map(td => (
                  <tr key={td.transformer_id} className="border-b border-gray-50">
                    <td className="py-1 pr-2 text-gray-700">{td.name || `#${td.transformer_id}`}</td>
                    <td className="py-1 pr-2 text-right text-gray-600">{td.kva_rating ?? '—'}</td>
                    <td className="py-1 pr-2 text-right text-gray-600">{td.meter_count}</td>
                    <td className="py-1 pr-2 text-right font-medium" style={{ color: VDROP_COLORS[td.drop_status] }}>
                      {td.placement_status === 'unplaceable'
                        ? '—'
                        : `${td.cumulative_drop_pct.toFixed(2)}%`}
                    </td>
                    <td className="py-1 pr-2 text-right text-gray-600">
                      {td.estimated_voltage_pu != null
                        ? td.estimated_voltage_pu.toFixed(4)
                        : '—'}
                    </td>
                    <td className="py-1">
                      <span
                        className="inline-block px-1.5 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: VDROP_COLORS[td.drop_status] + '20',
                          color: VDROP_COLORS[td.drop_status],
                        }}
                      >
                        {td.drop_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
